import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Modal } from '../ui/Modal';
import {
  ArrowLeft, UserPlus, KeyRound, Trash2, Copy, Loader2, ShieldCheck, RefreshCw, ShieldAlert, Check, Search, Clock, X,
} from 'lucide-react';
import {
  listUsers, updateUser, deleteUser, setUserPassword, createInvite,
  listWorkerTypes, listAccessRoles, inviteLink,
  listPending, approveUser, rejectUser,
  type AdminUser, type WorkerType, type AccessRole, type InviteInput,
} from '@/auth/adminClient';

/**
 * Экран «Доступы» — управление пользователями (видят только управляющие/руководитель):
 * раздать роли/города, пригласить сотрудника по ссылке, задать/сбросить пароль.
 */
export function AccessPage() {
  const setPage = useStore((s) => s.setPage);
  const branches = useStore((s) => s.branches);
  const employees = useStore((s) => s.employees);
  const toast = useStore((s) => s.toast);
  const currentUser = useStore((s) => s.currentUser);
  const isSuper = currentUser?.access_role === 'superadmin';

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pending, setPending] = useState<AdminUser[]>([]);
  const [pendingBusy, setPendingBusy] = useState<string | null>(null);
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [types, setTypes] = useState<WorkerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Карточка сотрудника → имя учётки, которая её уже заняла. Одна карточка = один вход:
  // иначе двое видят одни и те же игры как «свои» (реальная путаница у заказчика 23.07).
  const takenBy = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) if (u.employee_id) m.set(u.employee_id, u.full_name);
    return m;
  }, [users]);

  // Фильтр по имени (а также логину/почте) — без учёта регистра.
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('ru');
    if (!q) return users;
    return users.filter((u) =>
      u.full_name.toLocaleLowerCase('ru').includes(q)
      || (u.email ? u.email.toLocaleLowerCase('ru').includes(q) : false)
      || (u.login ? u.login.toLocaleLowerCase('ru').includes(q) : false)
    );
  }, [users, search]);

  const roleName = useMemo(() => new Map(roles.map((r) => [r.id, r.name])), [roles]);
  const typeName = useMemo(() => new Map(types.map((t) => [t.id, t.name])), [types]);
  const branchName = useMemo(() => new Map(branches.map((b) => [b.id, b.name])), [branches]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, r, t, p] = await Promise.all([listUsers(), listAccessRoles(), listWorkerTypes(), listPending()]);
      setUsers(u); setRoles(r); setTypes(t); setPending(p);
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось загрузить' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (isSuper) void refresh(); }, [isSuper]); // eslint-disable-line

  // Подтвердить/отклонить заявку из очереди «Ждут подтверждения».
  const doApprove = async (u: AdminUser) => {
    setPendingBusy(u.id);
    try {
      await approveUser(u.id);
      toast({ kind: 'success', text: `Доступ открыт: ${u.full_name}` });
      await refresh();
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось подтвердить' });
    } finally { setPendingBusy(null); }
  };
  const doReject = async (u: AdminUser) => {
    if (!confirm(`Отклонить заявку «${u.full_name}»? Регистрация будет удалена.`)) return;
    setPendingBusy(u.id);
    try {
      await rejectUser(u.id);
      toast({ kind: 'success', text: `Заявка отклонена: ${u.full_name}` });
      await refresh();
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось отклонить' });
    } finally { setPendingBusy(null); }
  };

  // Управление доступами/ролями — только у супер-админа (отдельный аккаунт).
  if (!isSuper) {
    return (
      <div className="h-full grid place-items-center bg-surface-0 text-ink-2 p-6 text-center">
        <div className="space-y-3 max-w-sm">
          <div className="text-lg font-semibold text-ink-1">Доступ только у супер-админа</div>
          <p className="text-sm">Этот раздел (пользователи, роли, права) доступен только под отдельным аккаунтом супер-админа.</p>
          <button className="btn-soft mx-auto" onClick={() => setPage('personnel')}>Назад</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface-0">
      <div className="min-h-12 py-1.5 sm:py-0 px-4 flex flex-wrap sm:flex-nowrap items-center gap-3 border-b border-line bg-surface-1 flex-shrink-0">
        <button className="icon-btn" onClick={() => setPage('personnel')} title="Назад к персоналу">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-base font-semibold flex items-center gap-2 min-w-0"><ShieldCheck className="w-4 h-4 text-accent flex-shrink-0" /> <span className="truncate">Доступы и пользователи</span></h1>
        {/* Поиск по имени (а также логину/почте) */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-ink-3 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            className="input h-8 w-56 text-sm pl-8"
            placeholder="Поиск по имени…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex-1" />
        {/* МАКЕТ: позже виден только супер-админу */}
        <button className="btn-soft" onClick={() => setPage('roles_matrix')} title="Роли и права (макет)">
          <ShieldAlert className="w-4 h-4" /> <span className="hidden sm:inline">Роли и права</span>
        </button>
        <button className="icon-btn" onClick={() => void refresh()} title="Обновить"><RefreshCw className="w-4 h-4" /></button>
        <button className="btn-primary" onClick={() => setInviteOpen(true)}>
          <UserPlus className="w-4 h-4" /> <span className="hidden sm:inline">Пригласить</span>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {/* «Замок 1»: заявки по общей ссылке, ждущие подтверждения. Показываем только если есть. */}
        {!loading && pending.length > 0 && (
          <div className="mb-4 rounded-xl border border-accent/40 bg-accent/5 p-3">
            <div className="flex items-center gap-2 mb-2 text-ink-1 font-semibold text-sm">
              <Clock className="w-4 h-4 text-accent" /> Ждут подтверждения
              <span className="text-[11px] font-normal text-ink-3">({pending.length})</span>
            </div>
            <div className="space-y-2">
              {pending.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-1 border border-line px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink-1 truncate">{u.full_name}</div>
                    <div className="text-[11px] text-ink-3 truncate">
                      {[u.login, typeName.get(u.worker_type ?? ''),
                        u.city_ids.map((c) => branchName.get(c) ?? c).join(', ') || null]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button className="btn-primary h-8" disabled={pendingBusy === u.id} onClick={() => void doApprove(u)}>
                    {pendingBusy === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Подтвердить
                  </button>
                  <button className="btn-soft h-8 text-status-refused" disabled={pendingBusy === u.id} onClick={() => void doReject(u)}>
                    <X className="w-4 h-4" /> Отклонить
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {loading ? (
          <div className="text-ink-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Загрузка…</div>
        ) : users.length === 0 ? (
          <p className="text-ink-3 text-sm">Пользователей пока нет. Управленцы появятся здесь после первого входа через Битрикс; линейных приглашайте кнопкой «Пригласить».</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-ink-3 text-left border-b border-line">
              <tr>
                <th className="px-3 py-2 font-semibold">Сотрудник</th>
                <th className="px-3 py-2 font-semibold w-[120px]">Вход</th>
                <th className="px-3 py-2 font-semibold w-[160px]">Роль</th>
                <th className="px-3 py-2 font-semibold">Города</th>
                <th className="px-3 py-2 font-semibold w-[110px]">Telegram</th>
                <th className="px-3 py-2 font-semibold w-[90px]">Активен</th>
                <th className="px-3 py-2 w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-b border-line/60 hover:bg-surface-1 cursor-pointer" onClick={() => setEditing(u)}>
                  <td className="px-3 py-2">
                    <div className="text-ink-1">{u.full_name}</div>
                    {u.email && <div className="text-[11px] text-ink-3">{u.email}</div>}
                  </td>
                  <td className="px-3 py-2 text-ink-2">
                    {u.kind === 'bitrix' ? 'Битрикс' : (u.login ? `Логин: ${u.login}` : 'Локальный')}
                  </td>
                  <td className="px-3 py-2">{u.access_role ? (roleName.get(u.access_role) ?? u.access_role) : <span className="text-ink-3">—</span>}</td>
                  <td className="px-3 py-2 text-ink-2">
                    {u.city_ids.length === 0 ? <span className="text-ink-3">все / не задан</span>
                      : u.city_ids.map((c) => branchName.get(c) ?? c).join(', ')}
                  </td>
                  <td className="px-3 py-2">
                    {u.telegram_linked ? (
                      <span className="inline-flex items-center gap-1 text-status-paid"><Check className="w-3.5 h-3.5" /> Привязан</span>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={u.is_active ? 'text-status-paid' : 'text-status-refused'}>{u.is_active ? 'да' : 'нет'}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-ink-3">{typeName.get(u.worker_type ?? '') ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* Есть пользователи, но фильтр ничего не нашёл */}
        {!loading && users.length > 0 && filteredUsers.length === 0 && (
          <p className="text-ink-3 text-sm mt-3">Никого не найдено.</p>
        )}
      </div>

      {editing && (
        <UserEditor
          user={editing} roles={roles} types={types} branches={branches} employees={employees} takenBy={takenBy}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void refresh(); }}
        />
      )}
      {inviteOpen && (
        <InviteModal
          roles={roles} types={types} branches={branches} employees={employees} takenBy={takenBy}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}

// ── Редактор пользователя ──────────────────────────────────
function UserEditor({
  user, roles, types, branches, employees, takenBy, onClose, onSaved,
}: {
  user: AdminUser; roles: AccessRole[]; types: WorkerType[];
  branches: { id: string; name: string }[]; employees: { id: string; full_name: string }[];
  /** карточка сотрудника → имя учётки, которая её уже заняла */
  takenBy: Map<string, string>;
  onClose: () => void; onSaved: () => void;
}) {
  const toast = useStore((s) => s.toast);
  const currentUser = useStore((s) => s.currentUser);
  // Служебной учётке супер-админа пароль задавать нельзя (ни себе, ни другой такой же) —
  // защита от дурака: восстановления по почте нет, запертого админа вернёт только БД.
  const targetIsSuper = user.access_role === 'superadmin' || user.id === currentUser?.id && currentUser?.access_role === 'superadmin';
  const [accessRole, setAccessRole] = useState(user.access_role ?? '');
  const [workerType, setWorkerType] = useState(user.worker_type ?? '');
  const [employeeId, setEmployeeId] = useState(user.employee_id ?? '');
  const [cityIds, setCityIds] = useState<string[]>(user.city_ids ?? []);
  const [isActive, setIsActive] = useState(user.is_active);
  const [notifyReg, setNotifyReg] = useState(user.notify_registrations ?? true);
  const [busy, setBusy] = useState(false);
  const [newPass, setNewPass] = useState('');

  const toggleCity = (id: string) =>
    setCityIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const save = async () => {
    setBusy(true);
    try {
      await updateUser(user.id, {
        access_role: accessRole || null, worker_type: workerType || null,
        employee_id: employeeId || null, city_ids: cityIds, is_active: isActive,
        notify_registrations: notifyReg,
      });
      toast({ kind: 'success', text: 'Сохранено' });
      onSaved();
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка' });
    } finally { setBusy(false); }
  };

  const doSetPassword = async () => {
    if (newPass.length < 6) { toast({ kind: 'warn', text: 'Пароль — минимум 6 символов' }); return; }
    try {
      await setUserPassword(user.id, newPass);
      setNewPass('');
      toast({ kind: 'success', text: 'Пароль задан. Передайте сотруднику.' });
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка' });
    }
  };

  const doDelete = async () => {
    if (!confirm(`Удалить пользователя «${user.full_name}»?`)) return;
    try {
      await deleteUser(user.id);
      toast({ kind: 'success', text: 'Пользователь удалён' });
      onSaved();
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка' });
    }
  };

  return (
    <Modal open onClose={onClose} title={user.full_name} size="md"
      footer={<>
        {/* Служебную учётку супер-админа удалять нельзя — кнопку скрываем. */}
        {!targetIsSuper && (
          <button className="btn-ghost text-status-refused mr-auto" onClick={doDelete}><Trash2 className="w-4 h-4" /> Удалить</button>
        )}
        <button className="btn-ghost" onClick={onClose}>Отмена</button>
        <button className="btn-primary" disabled={busy} onClick={save}>Сохранить</button>
      </>}>
      <div className="space-y-3">
        <div className="text-[11px] text-ink-3">
          {user.kind === 'bitrix' ? 'Вход через Битрикс' : (user.login ? `Локальный вход, логин: ${user.login}` : 'Локальный вход')}
        </div>

        <div><label className="field-label">Роль доступа</label>
          {/* Служебной учётке супер-админа роль менять нельзя — иначе в системе не
              останется супер-админа (защита от дурака). Список дизейблим. */}
          <select className="input" value={accessRole} onChange={(e) => setAccessRole(e.target.value)} disabled={targetIsSuper}>
            <option value="">— не задана —</option>
            {/* Супер-админа через интерфейс выдать нельзя (назначается только в БД); у самой
                служебной учётки роль показываем, но список задизейблен. */}
            {roles.filter((r) => r.id !== 'superadmin' || targetIsSuper).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {targetIsSuper && <p className="text-[11px] text-ink-3 mt-1">Служебная учётная запись — защищена от изменений.</p>}
        </div>

        <div><label className="field-label">Тип работника</label>
          <select className="input" value={workerType} onChange={(e) => setWorkerType(e.target.value)}>
            <option value="">— нет —</option>
            {types.map((t) => <option key={t.id} value={t.id}>{(t.icon ? t.icon + ' ' : '') + t.name}</option>)}
          </select>
        </div>

        <div><label className="field-label">Этот человек уже есть в «Персонале»?</label>
          <select className="input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">— нет, не выбирать —</option>
            {employees.map((e) => {
              // Карточку, занятую ДРУГОЙ учёткой, выбрать нельзя — видно, кем занята.
              const owner = takenBy.get(e.id);
              const busyByOther = !!owner && owner !== user.full_name;
              return (
                <option key={e.id} value={e.id} disabled={busyByOther}>
                  {e.full_name}{busyByOther ? ` — уже занят (${owner})` : ''}
                </option>
              );
            })}
          </select>
          <p className="text-[11px] text-ink-3 mt-1">
            Нужно только тем, кого <b>ставят на игры</b> (ведущий, диджей, хостес, водитель) — тогда он увидит
            свои игры, в том числе те, куда его вписали до регистрации. Менеджерам, кассирам, СММ и руководителям —
            оставьте «не выбирать». Это <b>не</b> выбор руководителя.
          </p>
          {!employeeId && (() => {
            // Подсказка: в «Персонале» уже есть человек с таким же ФИО — вероятно, это он.
            const norm = (x: string) => x.toLocaleLowerCase('ru').replace(/\s+/g, ' ').trim();
            const same = employees.find((e) => norm(e.full_name) === norm(user.full_name) && !takenBy.get(e.id));
            return same ? (
              <button type="button" onClick={() => setEmployeeId(same.id)}
                className="mt-1 text-[11px] text-accent hover:underline">
                Похоже, это «{same.full_name}» из «Персонала» — привязать?
              </button>
            ) : null;
          })()}
        </div>

        <div><label className="field-label">Города (если не выбрано — все/не ограничен)</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {branches.map((b) => (
              <button key={b.id} type="button" onClick={() => toggleCity(b.id)}
                className={`px-2.5 h-7 rounded-lg text-xs border transition-colors ${cityIds.includes(b.id) ? 'bg-accent text-white border-accent' : 'border-line text-ink-2 hover:bg-surface-2'}`}>
                {b.name}
              </button>
            ))}
          </div>
        </div>

        {/* Служебную учётку супер-админа деактивировать нельзя — переключатель скрываем. */}
        {!targetIsSuper && (
          <label className="flex items-center gap-2 text-sm cursor-pointer text-ink-2">
            <input type="checkbox" className="w-4 h-4 accent-accent" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Активен (может входить)
          </label>
        )}

        {/* Кому слать в Telegram заявки на подтверждение новых регистраций. Доступно для любой
            роли: включённая галочка делает человека назначенным подтверждающим (получает заявку
            и может нажать «Подтвердить/Отклонить»). */}
        <label className="flex items-start gap-2 text-sm cursor-pointer text-ink-2">
          <input type="checkbox" className="w-4 h-4 mt-0.5 accent-accent" checked={notifyReg} onChange={(e) => setNotifyReg(e.target.checked)} />
          <span>Получать в Telegram заявки на подтверждение новых регистраций</span>
        </label>

        {user.kind === 'local' && !targetIsSuper && (
          <div className="pt-3 border-t border-line">
            <label className="field-label flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Задать новый пароль</label>
            <div className="flex gap-2">
              <input className="input flex-1" type="text" placeholder="мин. 6 символов" value={newPass} onChange={(e) => setNewPass(e.target.value)} />
              <button className="btn-soft" onClick={doSetPassword}>Задать</button>
            </div>
            <p className="text-[11px] text-ink-3 mt-1">Пароль показывается один раз — передайте сотруднику лично.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Приглашение нового сотрудника ──────────────────────────
function InviteModal({
  roles, types, employees, takenBy, onClose,
}: {
  roles: AccessRole[]; types: WorkerType[];
  branches?: { id: string; name: string }[]; employees: { id: string; full_name: string }[];
  takenBy: Map<string, string>;
  onClose: () => void;
}) {
  const toast = useStore((s) => s.toast);
  const [accessRole, setAccessRole] = useState('performer');
  const [workerType, setWorkerType] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ link: string; temp?: string } | null>(null);

  const submit = async () => {
    setBusy(true);
    try {
      // ФИО/телефон/филиал заполняет сам сотрудник при регистрации — здесь только должность/роль.
      const input: InviteInput = {
        access_role: accessRole, worker_type: workerType || null,
        employee_id: employeeId || null, city_ids: [],
      };
      const { token } = await createInvite(input);
      setResult({ link: inviteLink(token) });
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка' });
    } finally { setBusy(false); }
  };

  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text);
    toast({ kind: 'success', text: 'Скопировано' });
  };

  return (
    <Modal open onClose={onClose} title="Пригласить сотрудника" size="md"
      footer={result ? (
        <button className="btn-primary" onClick={onClose}>Готово</button>
      ) : (<>
        <button className="btn-ghost" onClick={onClose}>Отмена</button>
        <button className="btn-primary" disabled={busy} onClick={submit}>Создать ссылку</button>
      </>)}>
      {result ? (
        <div className="space-y-3">
          <p className="text-sm text-ink-2">Ссылка-приглашение готова. Отправьте её сотруднику — он откроет и задаст логин/пароль.</p>
          <div className="flex gap-2">
            <input className="input flex-1 text-xs" readOnly value={result.link} onFocus={(e) => e.currentTarget.select()} />
            <button className="btn-soft" onClick={() => copy(result.link)}><Copy className="w-4 h-4" /> Копировать</button>
          </div>
          <p className="text-[11px] text-ink-3">Ссылка действует 14 дней.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[12px] text-ink-3">ФИО, телефон и филиал сотрудник укажет сам при регистрации. Здесь — только должность.</p>
          <div><label className="field-label">Должность</label>
            <select className="input" value={workerType} onChange={(e) => setWorkerType(e.target.value)}>
              <option value="">— нет —</option>
              {types.map((t) => <option key={t.id} value={t.id}>{(t.icon ? t.icon + ' ' : '') + t.name}</option>)}
            </select>
          </div>
          <div><label className="field-label">Роль доступа</label>
            <select className="input" value={accessRole} onChange={(e) => setAccessRole(e.target.value)}>
              {/* Супер-админа через приглашение выдать нельзя — только напрямую в БД. */}
              {roles.filter((r) => r.id !== 'superadmin').map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div><label className="field-label">Этот человек уже есть в «Персонале»? (обычно — нет)</label>
            <select className="input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">— нет, карточка создастся сама —</option>
              {employees.map((e) => {
                // Занятую другой учёткой карточку выбрать нельзя: один человек = один вход.
                const owner = takenBy.get(e.id);
                return (
                  <option key={e.id} value={e.id} disabled={!!owner}>
                    {e.full_name}{owner ? ` — уже занят (${owner})` : ''}
                  </option>
                );
              })}
            </select>
            <p className="text-[11px] text-ink-3 mt-1">
              Выбирайте, только если человека <b>уже ставили на игры</b> и у него есть карточка в «Персонале» —
              иначе появится двойник. Это <b>не</b> выбор руководителя. Во всех остальных случаях оставьте
              «карточка создастся сама».
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}
